import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useHistory } from 'react-router-dom';
import { useChatList } from 'components/layout/ChatListContext';
import FlagIssueButton from 'components/layout/FlagIssueButton';
import { useUnreadCountsContext } from 'components/UnreadCountsContext';
import { routers } from 'router/router';
import { todosApi } from 'services/camMirrorService';
import NotesModal from './NotesModal';
import NotificationsBell from './NotificationsBell';
import TodosModal from './TodosModal';
import './studentActionBar.css';

export type ActionViewerRole = 'counselor' | 'student' | 'parent';

const ICONS: Record<
  ActionViewerRole,
  Array<'chat' | 'notes' | 'todos' | 'documents' | 'notifications' | 'flag'>
> = {
  counselor: ['chat', 'notes', 'todos', 'documents', 'notifications', 'flag'],
  // student: documents opens the student's own documents page.
  // HEADER-DOCS-NOTES-V2: students can now write their own notes, so the Notes icon is shown.
  student: ['chat', 'notes', 'todos', 'documents', 'notifications', 'flag'],
  parent: ['chat', 'notifications'],
};

const actionButtonSx = {
  width: 40,
  height: 40,
  borderRadius: '999px',
  background: '#fff',
  border: '1px solid #DDE7F0',
  color: '#475A7E',
  '&:hover': { background: '#EDF3FE', color: '#3568DE', borderColor: '#C6D6E4' },
};

const ActionIconTip: React.FC<{ title: string; children: React.ReactElement }> = ({ title, children }) => (
  <Tooltip title={title} placement="bottom" enterDelay={200}>
    {children}
  </Tooltip>
);

const badgeSx = {
  '& .MuiBadge-badge': {
    background: '#3E73F0',
    color: '#fff',
    fontWeight: 700,
    border: '2px solid #F7FBFD',
  },
};

const StudentActionBar: React.FC<{
  studentId: string | number;
  viewerRole: ActionViewerRole;
  studentFirstName?: string;
  studentName?: string;
  classYear?: string | number | null;
  email?: string;
  showHome?: boolean;
}> = ({
  studentId,
  viewerRole,
  studentFirstName = '',
  studentName = '',
  classYear,
  email,
  showHome = false,
}) => {
  const history = useHistory();
  const { toggleChatList } = useChatList();
  const { unreadCounts } = useUnreadCountsContext();
  const [notesOpen, setNotesOpen] = useState(false);
  const [todosOpen, setTodosOpen] = useState(false);
  const [todoScope, setTodoScope] = useState<'counselor' | 'student'>(viewerRole === 'student' ? 'student' : 'counselor');
  const [todoUnread, setTodoUnread] = useState(0);
  const chatUnread = useMemo(
    () => Object.values(unreadCounts || {}).reduce((sum: number, count) => sum + Number(count || 0), 0),
    [unreadCounts],
  );

  const refreshTodoUnread = useCallback(() => {
    if (viewerRole === 'parent') return;
    if (studentId == null || studentId === '' || studentId === ':id') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    todosApi
      .list(studentId, 'student')
      .then((page) => {
        const unread = page.items.filter(
          (todo) =>
            todo.assignedToRole === 'student' &&
            todo.createdByRole === 'counselor' &&
            !todo.isCompleted &&
            !todo.readAt,
        ).length;
        setTodoUnread(unread);
      })
      .catch(() => undefined);
  }, [studentId, viewerRole]);

  useEffect(() => {
    refreshTodoUnread();
    const timer = window.setInterval(refreshTodoUnread, 60000);
    document.addEventListener('visibilitychange', refreshTodoUnread);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshTodoUnread);
    };
  }, [refreshTodoUnread]);

  if (studentId == null || studentId === '' || studentId === ':id') return null;

  const icons = ICONS[viewerRole];
  const displayName = studentName || studentFirstName || 'Student';
  const yearLabel = classYear ? `Class of ${classYear}` : '';

  return (
    <div className="gm-student-bar">
      <div className="gm-student-bar-id">
        {showHome && (
          <>
            <button type="button" className="gm-crumb-link" onClick={() => history.push(routers.dashBoard.path)}>
              Home
            </button>
            <span className="gm-crumb-sep">/</span>
          </>
        )}
        <span className="gm-crumb-name">{displayName}</span>
        {email ? <span className="gm-crumb-email">{email}</span> : null}
        {yearLabel ? <span className="gm-class-chip">{yearLabel}</span> : null}
      </div>
      <div className="gm-icon-row">
        {icons.includes('chat') && (
          <ActionIconTip title="Chat">
            <IconButton aria-label="Chat" onClick={toggleChatList} sx={actionButtonSx}>
              <Badge badgeContent={chatUnread} invisible={chatUnread === 0} sx={badgeSx}>
                <ChatBubbleOutlineIcon fontSize="small" />
              </Badge>
            </IconButton>
          </ActionIconTip>
        )}
        {icons.includes('notes') && (
          <ActionIconTip title="Notes">
            <IconButton aria-label="Notes" onClick={() => setNotesOpen(true)} sx={actionButtonSx}>
              <StickyNote2OutlinedIcon fontSize="small" />
            </IconButton>
          </ActionIconTip>
        )}
        {icons.includes('todos') && (
          <ActionIconTip title="ToDos">
            <IconButton
              aria-label="ToDos"
              onClick={() => {
                setTodoScope(viewerRole === 'student' ? 'student' : 'counselor');
                setTodosOpen(true);
              }}
              sx={actionButtonSx}
            >
              <Badge badgeContent={todoUnread} invisible={todoUnread === 0} sx={badgeSx}>
                <CheckBoxOutlinedIcon fontSize="small" />
              </Badge>
            </IconButton>
          </ActionIconTip>
        )}
        {icons.includes('documents') && (
          <ActionIconTip title="Documents">
            <IconButton
              aria-label="Documents"
              onClick={() => {
                if (viewerRole === 'student') {
                  history.push(routers.documents.path);
                  return;
                }
                history.push({
                  pathname: routers.viewStudent.path.replace(':id', String(studentId)),
                  search: '?tab=10',
                });
              }}
              sx={actionButtonSx}
            >
              <DescriptionOutlinedIcon fontSize="small" />
            </IconButton>
          </ActionIconTip>
        )}
        {icons.includes('notifications') && (
          <NotificationsBell
            onOpenTodos={
              icons.includes('todos')
                ? () => {
                    setTodoScope('student');
                    setTodosOpen(true);
                  }
                : undefined
            }
          />
        )}
        {icons.includes('flag') && <FlagIssueButton studentId={studentId} viewerRole={viewerRole} />}
      </div>
      {icons.includes('notes') && (
        <NotesModal
          open={notesOpen}
          onClose={() => setNotesOpen(false)}
          studentId={studentId}
          studentFirstName={studentFirstName || displayName.split(' ')[0]}
          viewerRole={viewerRole === 'student' ? 'student' : 'counselor'}
        />
      )}
      {icons.includes('todos') && viewerRole !== 'parent' && (
        <TodosModal
          open={todosOpen}
          onClose={() => setTodosOpen(false)}
          studentId={studentId}
          studentFirstName={studentFirstName || displayName.split(' ')[0]}
          viewerRole={viewerRole === 'counselor' ? 'counselor' : 'student'}
          openScope={todoScope}
          onActivity={refreshTodoUnread}
        />
      )}
    </div>
  );
};

export default StudentActionBar;
